import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { 
  Plane, GraduationCap, Users, Globe, Award, CheckCircle, Star, 
  BookOpen, Mic, MessageSquare, Shield, Phone, Mail, MapPin,
  Play, Check, ArrowRight, ChevronRight, Trophy, Heart, UserCheck, Headphones,
  Target, Zap, Gift, Calendar, TrendingUp, Sparkles, Crown, Medal
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', counselor_name: '' });
  const [loading, setLoading] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  const testimonials = [
    { name: 'Elif Y.', role: 'Emirates Kabin Memuru', text: 'AirAcademia sayesinde havacılık İngilizcesini çok hızlı öğrendim. 3 ayda Emirates mülakatını geçtim!', avatar: '👩‍✈️' },
    { name: 'Mehmet K.', role: 'THY Kabin Amiri', text: 'Yapılandırılmış eğitim programı ve interaktif dersler sayesinde kendimi çok geliştirdim.', avatar: '👨‍✈️' },
    { name: 'Zeynep D.', role: 'Qatar Airways', text: 'Speaking ve listening dersleri gerçek mülakatlara harika hazırladı. Hayalimdeki işe kavuştum!', avatar: '👩‍✈️' },
    { name: 'Ayşe T.', role: 'Pegasus Kabin Memuru', text: 'Sıfırdan başladım, 6 ayda B2 seviyesine ulaştım. Mülakatımda çok rahat hissettim.', avatar: '👩‍✈️' },
    { name: 'Burak S.', role: 'SunExpress', text: 'Roleplay senaryoları gerçek uçuş ortamına hazırladı. Eğitmenler çok ilgili ve profesyonel.', avatar: '👨‍✈️' },
    { name: 'Selin A.', role: 'Etihad Airways', text: 'Online eğitim bu kadar etkili olabilir mi diye düşünüyordum. Şimdi körfezde çalışıyorum!', avatar: '👩‍✈️' },
    { name: 'Can M.', role: 'THY Kabin Memuru', text: 'Havacılık terminolojisini bu kadar detaylı öğreten başka platform görmedim.', avatar: '👨‍✈️' },
    { name: 'Deniz Ö.', role: 'AnadoluJet', text: 'Danışmanlar her soruma anında cevap verdi. Motivasyonum hiç düşmedi.', avatar: '👩‍✈️' },
    { name: 'Emre B.', role: 'Corendon Airlines', text: 'Grammar ve pronunciation derslerinde çok fazla pratik yaptım. Mülakatta fark yarattı.', avatar: '👨‍✈️' },
    { name: 'Gizem K.', role: 'FlyDubai', text: 'Modüler sistem sayesinde kendi hızımda ilerledim. Dubai hayalim gerçek oldu!', avatar: '👩‍✈️' },
  ];
  
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [testimonials.length]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData, { withCredentials: true });
      const userData = response.data.user;
      const sessionToken = response.data.session_token;
      if (sessionToken) localStorage.setItem('session_token', sessionToken);
      setUser(userData);
      toast.success('Giriş başarılı!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordPhone) {
      toast.error('Telefon numarası gereklidir');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { phone: forgotPasswordPhone });
      toast.success('Yeni şifreniz SMS olarak gönderildi');
      setShowForgotPassword(false);
      setForgotPasswordPhone('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setForgotPasswordLoading(false);
    }
  };
  
  const handleRegisterRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register-request`, registerData);
      toast.success('Kayıt talebiniz alındı! Admin onayından sonra size bilgi verilecektir.');
      setShowAuth(false);
      setRegisterData({ first_name: '', last_name: '', email: '', password: '', phone: '', counselor_name: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kayıt talebi gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  // Auth Modal
  if (showAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-4">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur" data-testid="auth-card">
          <CardHeader className="text-center py-6">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Plane className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold">
                <span className="text-blue-600">Kariyer</span>
                <span className="text-blue-800"> Online</span>
              </span>
            </div>
            <CardDescription className="text-gray-500">Kabin Memuru Eğitim Platformu</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Giriş Yap</TabsTrigger>
                <TabsTrigger value="register">Kayıt Talebi</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div><Label>E-posta</Label><Input type="email" placeholder="ornek@email.com" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required /></div>
                  <div><Label>Şifre</Label><Input type="password" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required /></div>
                  <div className="flex justify-end">
                    <button 
                      type="button" 
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Şifremi Unuttum
                    </button>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" disabled={loading}>{loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}</Button>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800">⚠️ Kayıt işlemi admin onayı gerektirir.</p>
                </div>
                <form onSubmit={handleRegisterRequest} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Ad</Label><Input placeholder="Adınız" value={registerData.first_name} onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })} required /></div>
                    <div><Label>Soyad</Label><Input placeholder="Soyadınız" value={registerData.last_name} onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })} required /></div>
                  </div>
                  <div><Label>E-posta</Label><Input type="email" placeholder="ornek@email.com" value={registerData.email} onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })} required /></div>
                  <div><Label>Telefon</Label><Input type="tel" placeholder="05XX XXX XX XX" value={registerData.phone} onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })} required /></div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" disabled={loading}>{loading ? 'Gönderiliyor...' : '📩 Kayıt Talebi Gönder'}</Button>
                </form>
              </TabsContent>
            </Tabs>
            <Button variant="ghost" className="w-full mt-4 text-gray-500" onClick={() => setShowAuth(false)}>← Ana Sayfaya Dön</Button>
          </CardContent>
        </Card>
        
        {/* Şifremi Unuttum Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowForgotPassword(false)}>
            <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-xl">Şifremi Unuttum</CardTitle>
                <CardDescription>Telefon numaranızı girin, yeni şifrenizi SMS olarak göndereceğiz.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label>Telefon Numarası</Label>
                    <Input 
                      type="tel" 
                      placeholder="05XX XXX XX XX" 
                      value={forgotPasswordPhone}
                      onChange={(e) => setForgotPasswordPhone(e.target.value)}
                      required
                      data-testid="forgot-password-phone"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setShowForgotPassword(false)}
                    >
                      İptal
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
                      disabled={forgotPasswordLoading}
                      data-testid="forgot-password-submit"
                    >
                      {forgotPasswordLoading ? 'Gönderiliyor...' : 'SMS Gönder'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">
                <span className="text-blue-600">Kariyer</span>
                <span className="text-blue-800"> Online</span>
              </span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#ozellikler" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">Özellikler</a>
              <a href="#gamification" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">Eğitim</a>
              <a href="#egitimler" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">Eğitimler</a>
              <a href="#referanslar" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">Referanslar</a>
            </div>
            
            <div className="flex items-center gap-3">
              <a href="tel:+905536838375" className="hidden lg:flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4 text-blue-600" />
                0 553 683 8375
              </a>
              <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-sm px-5" onClick={() => setShowAuth(true)}>
                Giriş Yap
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white rounded-full px-4 py-2 text-sm font-medium mb-8">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                Profesyonel Kabin Memuru Eğitimi
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight text-white">
                Türk Hava Kurumu Üniversitesi
              </h1>
              
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                Sertifikalı Kabin Memurluğu Eğitimi
              </h2>
              
              <p className="text-xl md:text-2xl font-semibold text-blue-100 mb-6">
                Kabin Memuru Ol, Gökyüzünü Fethet
              </p>
              
              <p className="text-lg text-blue-100 mb-10 max-w-xl leading-relaxed">
                Havacılık İngilizcesi, mülakat hazırlığı ve kariyer gelişimi. İnteraktif eğitim sistemi ile havacılık kariyerine hazırlan.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 mb-10">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 font-semibold text-base px-8 py-6 rounded-xl shadow-lg shadow-yellow-500/25" onClick={() => setShowAuth(true)}>
                  Hemen Başla
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 text-base px-8 py-6 rounded-xl" onClick={() => document.getElementById('ozellikler').scrollIntoView({ behavior: 'smooth' })}>
                  <Play className="w-4 h-4 mr-2" />
                  Keşfet
                </Button>
              </div>
              
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-white/10">
                {[
                  { value: '5,000+', label: 'Aktif Öğrenci', icon: Users },
                  { value: '50,000+', label: 'Tamamlanan Ders', icon: BookOpen },
                  { value: '%92', label: 'Başarı Oranı', icon: TrendingUp },
                  { value: '50+', label: 'Havayolu', icon: Globe },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center lg:text-left">
                    <div className="flex items-center gap-2 justify-center lg:justify-start mb-1">
                      <stat.icon className="w-4 h-4 text-yellow-400" />
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                    </div>
                    <p className="text-xs text-blue-200">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right - Feature Cards */}
            <div className="relative hidden lg:block">
              <div className="relative w-full h-[500px]">
                {/* Main Card - Leaderboard */}
                <div className="absolute top-0 right-0 w-72 bg-white rounded-2xl shadow-2xl p-5 animate-bounce" style={{ animationDuration: '3s' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">En Başarılı Öğrenciler</p>
                      <p className="text-xs text-gray-500">Bu Hafta</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: 'Ayşe Y.', score: '%96', rank: 1 },
                      { name: 'Mehmet K.', score: '%94', rank: 2 },
                      { name: 'Zeynep D.', score: '%91', rank: 3 },
                    ].map((user, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-yellow-400 text-yellow-900' : idx === 1 ? 'bg-gray-300 text-gray-700' : 'bg-amber-600 text-white'}`}>
                          {user.rank}
                        </span>
                        <span className="flex-1 text-sm font-medium text-gray-700">{user.name}</span>
                        <span className="text-sm font-bold text-blue-600">{user.score}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Spin Wheel Card */}
                <div className="absolute top-40 left-0 w-64 bg-white rounded-2xl shadow-2xl p-4 animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Mic className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Speaking Test</p>
                      <p className="text-xs text-green-600 font-medium">%95 Başarı! 🎉</p>
                    </div>
                  </div>
                </div>
                
                {/* Daily Tasks Card */}
                <div className="absolute bottom-20 left-10 w-64 bg-white rounded-2xl shadow-2xl p-4 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1s' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Modül İlerlemesi</p>
                      <p className="text-xs text-gray-500">8/12 Tamamlandı</p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full" style={{ width: '66%' }}></div>
                  </div>
                </div>
                
                {/* Career Level Card */}
                <div className="absolute bottom-0 right-10 w-56 bg-white rounded-2xl shadow-2xl p-4 animate-bounce" style={{ animationDuration: '4.5s', animationDelay: '1.5s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                      <Award className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Seviye: B1+</p>
                      <p className="text-xs text-blue-600">Intermediate ✈️</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campus Interview Rehearsal Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 rounded-full px-4 py-2 text-sm font-medium mb-4">
                <Plane className="w-4 h-4" />
                Kampüs Eğitimi
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Türk Hava Kurumu Üniversitesi Kampüsünde,{' '}
                <span className="text-amber-600">Mülakat Provası</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Gerçek mülakat ortamını deneyimleyin. Profesyonel eğitmenler eşliğinde, birebir mülakat simülasyonları ile kendinizi test edin ve güveninizi artırın.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Gerçekçi mülakat senaryoları',
                  'Birebir geri bildirim',
                  'Beden dili ve iletişim koçluğu',
                  'Video kayıt ve analiz'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                onClick={() => setShowAuth(true)}
              >
                Detaylı Bilgi Al
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* Video Placeholder */}
            <div className="relative">
              <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-xl border border-gray-200">
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg mb-4 cursor-pointer hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                  <p className="text-gray-500 font-medium">Kampüs Mülakat Provası</p>
                  <p className="text-gray-400 text-sm">Tanıtım Videosu</p>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 w-12 h-12 bg-white/80 backdrop-blur rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-amber-600" />
                </div>
                <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur rounded-lg px-3 py-2">
                  <p className="text-xs font-semibold text-gray-700">THK Üniversitesi</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mock-up Training Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-2 text-sm font-medium mb-4">
              <Plane className="w-4 h-4" />
              Uygulamalı Eğitim
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Türk Hava Kurumu Üniversitesi Kampüsünde,{' '}
              <span className="text-blue-600">Mock-up Eğitimi</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Gerçek uçak kabinini simüle eden mock-up alanlarımızda uygulamalı eğitim alın. Teorik bilgileri pratiğe dökün.
            </p>
          </div>
          
          {/* Image Slider Placeholder */}
          <div className="relative">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: 'Kabin Simülasyonu', desc: 'Gerçekçi kabin ortamı' },
                { title: 'Servis Eğitimi', desc: 'Yolcu hizmetleri pratiği' },
                { title: 'Acil Durum Tatbikatı', desc: 'Güvenlik prosedürleri' }
              ].map((item, idx) => (
                <div key={idx} className="group relative">
                  <div className="aspect-[4/3] bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                        idx === 0 ? 'bg-gradient-to-br from-blue-500 to-indigo-500' :
                        idx === 1 ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                        'bg-gradient-to-br from-amber-500 to-red-500'
                      }`}>
                        {idx === 0 && <Plane className="w-8 h-8 text-white" />}
                        {idx === 1 && <Heart className="w-8 h-8 text-white" />}
                        {idx === 2 && <Shield className="w-8 h-8 text-white" />}
                      </div>
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{item.title}</h3>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                      <div className="mt-4 px-3 py-1 bg-white/80 backdrop-blur rounded-full">
                        <p className="text-xs text-gray-600">Görsel Eklenecek</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Slider Navigation Placeholder */}
            <div className="flex justify-center gap-2 mt-8">
              {[0, 1, 2].map((idx) => (
                <button
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${idx === 0 ? 'bg-blue-600 w-8' : 'bg-gray-300'}`}
                />
              ))}
            </div>
          </div>
          
          <div className="text-center mt-10">
            <Button 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              onClick={() => setShowAuth(true)}
            >
              Mock-up Eğitimi Hakkında Bilgi Al
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Eğitim Avantajları Section */}
      <section id="gamification" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-2 text-sm font-medium mb-4">
              <GraduationCap className="w-4 h-4" />
              Eğitim Sistemi
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Kariyer Hedeflerinize Ulaşın
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Yapılandırılmış müfredat, interaktif dersler ve sürekli gelişim takibi ile havacılık kariyerinize hazırlanın.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: BookOpen, 
                title: 'Modüler Eğitim', 
                desc: 'Seviyenize uygun, adım adım ilerleyen kapsamlı müfredat.',
                color: 'from-blue-500 to-indigo-500',
                stat: '12+ Modül'
              },
              { 
                icon: Mic, 
                title: 'Speaking Pratiği', 
                desc: 'Gerçek mülakat senaryolarıyla konuşma becerinizi geliştirin.',
                color: 'from-purple-500 to-pink-500',
                stat: 'AI Destekli'
              },
              { 
                icon: Headphones, 
                title: 'Listening Dersleri', 
                desc: 'Native speaker kayıtlarıyla dinleme yeteneğinizi artırın.',
                color: 'from-green-500 to-emerald-500',
                stat: '100+ Ses'
              },
              { 
                icon: Trophy, 
                title: 'İlerleme Takibi', 
                desc: 'Gelişiminizi takip edin, güçlü ve zayıf yönlerinizi görün.',
                color: 'from-yellow-500 to-orange-500',
                stat: 'Detaylı Raporlar'
              },
              { 
                icon: Target, 
                title: 'Hedef Odaklı', 
                desc: 'Kişisel hedefler belirleyin ve ilerlemenizi ölçün.',
                color: 'from-amber-500 to-red-500',
                stat: 'Kişiselleştirilmiş'
              },
              { 
                icon: Globe, 
                title: 'Havacılık İngilizcesi', 
                desc: 'Sektöre özel terminoloji ve iletişim becerileri.',
                color: 'from-cyan-500 to-blue-500',
                stat: '500+ Terim'
              },
              { 
                icon: MessageSquare, 
                title: 'Roleplay Senaryoları', 
                desc: 'Gerçekçi uçuş senaryolarında pratik yapın.',
                color: 'from-indigo-500 to-purple-500',
                stat: '50+ Senaryo'
              },
              { 
                icon: Award, 
                title: 'Sertifika Programı', 
                desc: 'Eğitimi tamamlayın, yetkinlik sertifikanızı alın.',
                color: 'from-rose-500 to-pink-500',
                stat: 'Onaylı Sertifika'
              },
            ].map((item, idx) => (
              <div key={idx} className="group bg-white p-6 rounded-2xl border border-gray-100 hover:border-transparent hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 transition-opacity`}></div>
                <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 mb-3">{item.desc}</p>
                <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full bg-gradient-to-r ${item.color} text-white`}>
                  {item.stat}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="ozellikler" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium text-sm mb-3">EĞİTİM ÖZELLİKLERİ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Kapsamlı Eğitim İçerikleri
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              ICAO standartlarında havacılık İngilizcesi ve mülakat hazırlığı.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Mic, title: 'Speaking Pratikleri', desc: 'AI destekli konuşma pratiği ve telaffuz analizi.', color: 'bg-blue-500' },
              { icon: Headphones, title: 'Listening Egzersizleri', desc: 'Anons dinleme ve anlama testleri.', color: 'bg-purple-500' },
              { icon: MessageSquare, title: 'Rol-Play Senaryoları', desc: 'Gerçekçi yolcu hizmetleri diyalogları.', color: 'bg-green-500' },
              { icon: Shield, title: 'Acil Durum Eğitimi', desc: 'İnteraktif güvenlik prosedürleri.', color: 'bg-red-500' },
              { icon: UserCheck, title: 'Mülakat Simülasyonu', desc: '64 soru ile kapsamlı hazırlık.', color: 'bg-amber-500' },
              { icon: GraduationCap, title: 'Sertifika Programı', desc: 'Resmi onaylı başarı belgesi.', color: 'bg-indigo-500' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all group">
                <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Program Section */}
      <section id="egitimler" className="py-20 bg-gradient-to-br from-blue-900 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-blue-300 font-medium text-sm mb-3">EĞİTİM PROGRAMI</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                8 Aşamalı Profesyonel Müfredat
              </h2>
              <p className="text-blue-100 mb-8 leading-relaxed">
                A1'den C2'ye kadar İngilizce eğitimi, her aşamada mil kazanarak ilerleme ve kariyer danışmanlığı.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'ICAO standartlarında havacılık İngilizcesi',
                  'Kişiselleştirilmiş öğrenme deneyimi',
                  'Profesyonel mülakat simülasyonları',
                  'Kişisel danışman desteği',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-blue-100">{item}</span>
                  </div>
                ))}
              </div>
              
              <Button className="bg-white text-blue-900 hover:bg-blue-50" onClick={() => setShowAuth(true)}>
                Eğitime Başla
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { num: '01', title: 'Temel Kelimeler', icon: BookOpen, duration: '2 Hafta' },
                { num: '02', title: 'ICAO Alfabe', icon: Globe, duration: '1 Hafta' },
                { num: '03', title: 'Diyaloglar', icon: MessageSquare, duration: '3 Hafta' },
                { num: '04', title: 'Anonslar', icon: Mic, duration: '2 Hafta' },
                { num: '05', title: 'Acil Durumlar', icon: Shield, duration: '2 Hafta' },
                { num: '06', title: 'Mülakat', icon: UserCheck, duration: '4 Hafta' },
                { num: '07', title: 'Servis', icon: Heart, duration: '3 Hafta' },
                { num: '08', title: 'Final Sınavı', icon: Trophy, duration: 'Sertifika' },
              ].map((item, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-4 hover:bg-white/20 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-xs text-blue-300 font-semibold">AŞAMA {item.num}</span>
                      <p className="font-semibold text-white text-sm">{item.title}</p>
                    </div>
                  </div>
                  <p className="text-xs text-yellow-400 mt-2 text-right">{item.duration}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="referanslar" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-blue-600 font-medium text-sm mb-3">REFERANSLAR</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Başarı Hikayeleri
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 text-center">
              <div className="text-6xl mb-6">{testimonials[activeTestimonial].avatar}</div>
              <div className="flex justify-center gap-1 mb-6">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                "{testimonials[activeTestimonial].text}"
              </p>
              <div>
                <p className="font-bold text-gray-900">{testimonials[activeTestimonial].name}</p>
                <p className="text-sm text-blue-600">{testimonials[activeTestimonial].role}</p>
              </div>
              
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${idx === activeTestimonial ? 'bg-blue-600 w-8' : 'bg-gray-300'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white rounded-full px-4 py-2 text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            Kariyerinize İlk Adımı Atın
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Hayalinizdeki Kariyere Uçun
          </h2>
          
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Kapsamlı eğitim programımız ile havacılık kariyerinize hemen başlayın!
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 font-semibold text-base px-8" onClick={() => setShowAuth(true)}>
              Hemen Kaydol
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <span className="text-blue-200 text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Danışmanlarımız size ulaşsın
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Plane className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">
                  <span className="text-blue-400">Kariyer</span>
                  <span className="text-white"> Online</span>
                </span>
              </div>
              <p className="text-gray-400 text-sm">
                Profesyonel kabin memurluğu eğitimi platformu.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm">Özellikler</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Modüler Eğitim</li>
                <li>İlerleme Takibi</li>
                <li>Canlı Dersler</li>
                <li>Sertifika Programı</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm">Eğitimler</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Speaking Pratik</li>
                <li>Listening Egzersiz</li>
                <li>Mülakat Hazırlık</li>
                <li>Acil Durum</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm">İletişim</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" />info@airacademia.com</li>
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" />0 553 683 8375</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" />Dubai & Ankara</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
            <p>© 2025 Kariyer Online. Tüm Hakları Saklıdır.</p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-white">Gizlilik</a>
              <a href="#" className="hover:text-white">Şartlar</a>
              <a href="#" className="hover:text-white">KVKK</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
